/**
 * Tokens — the ONLY coupling between surfaces and infrastructure. Defined once
 * here, imported everywhere else (tokens are identity-based). Plus the SPA's
 * mutating-call transport: every mutating /api call carries the CSRF header.
 */

import { createToken, type CommandTransport, type PushChannel, type Router } from "@platform/web-kit"
import type { ApplicationStore, AssetStore, BreadcrumbBus, EvidenceStore, GenerationRunStore, IngestStore, OpportunityStore, OrgStore, SearchRunStore, SectionNavBus, ShellSession, WorkshopStore } from "./domain"
import type { CareerI18n } from "../i18n"

export const SearchRunStoreToken = createToken<SearchRunStore>("career-search-run-store")
export const IngestStoreToken = createToken<IngestStore>("career-ingest-store")
export const EvidenceStoreToken = createToken<EvidenceStore>("career-evidence-store")
export const AssetStoreToken = createToken<AssetStore>("career-asset-store")
export const WorkshopStoreToken = createToken<WorkshopStore>("career-workshop-store")
export const OpportunityStoreToken = createToken<OpportunityStore>("career-opportunity-store")
export const OrgStoreToken = createToken<OrgStore>("career-org-store")
export const ApplicationStoreToken = createToken<ApplicationStore>("career-application-store")
export const GenerationRunStoreToken = createToken<GenerationRunStore>("career-generation-run-store")
export const RouterToken = createToken<Router>("career-router")
export const I18nToken = createToken<CareerI18n>("career-i18n")
export const SessionToken = createToken<ShellSession>("career-session")
export const PushToken = createToken<PushChannel>("career-push")
export const BreadcrumbToken = createToken<BreadcrumbBus>("career-breadcrumb")
export const SectionNavToken = createToken<SectionNavBus>("career-section-nav")

/** CSRF: every mutating /api call carries this header (BFF rejects it if absent). */
export const CSRF_HEADER = "X-Career-Csrf"

/**
 * The dead-session reaction, overridable in tests. Default: navigate to the BFF
 * login (a full page-load that clears the stale logged-in chrome). SSO silently
 * re-completes if the Keycloak session is still alive, else the login form shows.
 * The `returnTo` (route + query + hash) brings the user back to EXACTLY where they
 * were — surfaces that persist a local draft (e.g. the value cardgame) round-trip
 * through the re-auth untouched, so in-progress work survives.
 */
export const sessionExpiryHandler: { redirect: () => void } = {
  redirect() {
    if (typeof location === "undefined") return
    // Preserve the full location so the post-login callback lands the user back on the
    // exact surface + query + in-page anchor they were on (see the BFF `returnTo` round-trip).
    const returnTo = location.pathname + location.search + location.hash
    location.assign(`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`)
  },
}

/** The window event the shell listens for to show the "your session expired" re-auth prompt. */
export const SESSION_EXPIRED_EVENT = "career:session-expired"

/** Once handled, ignore further dead-session signals within this page load (no redirect/prompt spam). */
let sessionExpiredNotified = false

/**
 * Redirect-loop guard. A genuine dead session is healed by ONE full-page re-auth
 * round-trip (SSO usually re-completes silently and lands the user back where they
 * were). But if the returned session immediately 401s again — e.g. the api is
 * rejecting a still-valid token — a blind auto-redirect would hard-loop through the
 * login endpoint. We stamp the attempt time in `sessionStorage` (survives the
 * navigation, scoped to the tab); a second expiry within the window falls back to the
 * calm inline prompt instead of redirecting, so the user is never stuck reloading.
 */
const RELOGIN_ATTEMPT_KEY = "career:relogin-attempt-at"
const RELOGIN_LOOP_WINDOW_MS = 30_000

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage
  } catch {
    return null // Storage can throw in privacy modes / sandboxed frames.
  }
}

/** True when we already bounced through login very recently — treat the next 401 as a loop. */
function reloginLoopDetected(): boolean {
  const store = safeSessionStorage()
  const at = Number(store?.getItem(RELOGIN_ATTEMPT_KEY) ?? "")
  return Number.isFinite(at) && at > 0 && Date.now() - at < RELOGIN_LOOP_WINDOW_MS
}

/** Record that we are about to auto-redirect to login (so a fast re-expiry is seen as a loop). */
function markReloginAttempt(): void {
  safeSessionStorage()?.setItem(RELOGIN_ATTEMPT_KEY, String(Date.now()))
}

/** A healthy api response clears the loop marker so the NEXT genuine expiry redirects normally. */
function clearReloginAttempt(): void {
  safeSessionStorage()?.removeItem(RELOGIN_ATTEMPT_KEY)
}

/** Test-only: re-arm the one-shot guard (and clear the loop marker) so a fresh case can observe it again. */
export function resetSessionExpiredForTesting(): void {
  sessionExpiredNotified = false
  clearReloginAttempt()
}

// ─── Insufficient-credits (402) — one shared reaction, never per-surface ────────
//
// A metered action the caller can't afford returns HTTP 402 `PAYMENT_REQUIRED`. Rather
// than teach every surface to recognise it, the SHARED fetch layer raises a global
// signal (a window CustomEvent) that the shell turns into ONE inline notice with a link
// to /credits — the same "handle-once-globally" shape as {@link reactToSessionExpiry}.

/** The window event the shell listens for to show the "top up your balance" notice. */
export const PAYMENT_REQUIRED_EVENT = "career:payment-required"
/** The window event the credits surface fires once a purchase has landed, so the shell chip refreshes. */
export const CREDITS_UPDATED_EVENT = "career:credits-updated"

/**
 * What the shared notice carries. Two shapes of the SAME product state (you need credits):
 * - `insufficient` (HTTP 402) — you have SOME balance but not enough for THIS action; the api
 *   sends `{ action, required, balance }`.
 * - `blocked` (HTTP 403, the entitlement gate) — you're not entitled at all (zero balance, no
 *   active subscription), so nothing metered even reaches the 402 floor. This is the literal
 *   first-run case: `gateMiddleware` denies before `meteredGate`.
 */
export interface PaymentRequiredDetail {
  kind?: "insufficient" | "blocked"
  action?: string
  required?: number
  balance?: number
}

/** Thrown by the shared client on a 402/entitlement-403 so a caller CAN branch on it; the global notice is already raised. */
export class PaymentRequiredError extends Error {
  constructor(readonly detail: PaymentRequiredDetail) {
    super("payment required")
    this.name = "PaymentRequiredError"
  }
}

/** True when the call was blocked for lack of credits (402 insufficient OR the entitlement 403). */
export function isPaymentRequired(e: unknown): e is PaymentRequiredError {
  return e instanceof PaymentRequiredError
}

/** The api's entitlement-gate 403 body (must be distinguished from cross-user / other 403s). */
const isNotEntitledBody = (body: { code?: string; message?: string } | null): boolean =>
  body?.code === "FORBIDDEN" && typeof body.message === "string" && /not entitled/i.test(body.message)

/**
 * A metered `/api/*` call that fails for lack of credits → raise the shared "top up" signal so
 * the shell shows ONE inline notice (never crediting client-side; the user tops up on /credits).
 * Covers BOTH the 402 insufficient floor AND the entitlement-gate 403 (zero-balance first run).
 * Other 403s (cross-user isolation, etc.) are NOT swallowed — they fall through to a normal error.
 * Returns the detail so the caller can throw a {@link PaymentRequiredError} carrying it.
 */
async function reactToEntitlementBlock(res: Response, path: string): Promise<PaymentRequiredDetail | null> {
  if (path.startsWith("/api/auth/") || (res.status !== 402 && res.status !== 403)) return null
  const body = (await res.clone().json().catch(() => null)) as { code?: string; message?: string; data?: PaymentRequiredDetail } | null
  let detail: PaymentRequiredDetail | null = null
  if (res.status === 402) detail = { kind: "insufficient", ...(body?.data ?? {}) }
  else if (isNotEntitledBody(body)) detail = { kind: "blocked", balance: 0 }
  if (!detail) return null
  if (typeof globalThis.dispatchEvent === "function") {
    globalThis.dispatchEvent(new CustomEvent<PaymentRequiredDetail>(PAYMENT_REQUIRED_EVENT, { detail }))
  }
  return detail
}

/**
 * Any `/api/*` call that comes back 401 means the browser session is dead: the BFF
 * ALREADY attempted a transparent `refresh_token` refresh server-side before it
 * returned 401 (see `bff.ts` `resolveAuth` → `getAccessToken`), so a 401 reaching the
 * client is not something a client-side retry can fix — re-auth is genuinely needed.
 *
 * Operator directive: don't strand the user behind an inline prompt — ROUTE them to
 * login and bring them back. So on the first non-auth 401 we auto-redirect through the
 * BFF login (`sessionExpiryHandler.redirect`), preserving route + query + hash as
 * `returnTo`; SSO usually re-completes silently and lands them exactly where they were,
 * and surfaces that persist a local draft survive the round-trip. Only if we detect a
 * redirect LOOP (a fresh 401 right after a re-auth attempt — e.g. the api is rejecting a
 * valid token) do we fall back to the calm inline prompt via {@link SESSION_EXPIRED_EVENT}
 * so the user isn't stuck reloading. Never fires for `/api/auth/*` (that would trigger
 * during the login round-trip itself).
 */
async function reactToSessionExpiry(res: Response, path: string): Promise<void> {
  if (res.status !== 401 || sessionExpiredNotified || path.startsWith("/api/auth/")) return
  sessionExpiredNotified = true
  if (reloginLoopDetected()) {
    // We just came back from login and 401'd again — a hard redirect would loop.
    // Show the inline prompt instead (its »Neu anmelden« still offers a manual retry).
    if (typeof globalThis.dispatchEvent === "function") {
      globalThis.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
    }
    return
  }
  markReloginAttempt()
  sessionExpiryHandler.redirect()
}

/** Thrown on a data-plane 401 (session dead). The shell already raised the re-auth prompt, so a
 *  caller catching this should NOT also render a raw error — just preserve/restore its state. */
export class SessionExpiredError extends Error {
  constructor() {
    super("session expired")
    this.name = "SessionExpiredError"
  }
}

/** True when a call failed because the browser session expired (the shell shows the re-auth prompt). */
export function isSessionExpired(e: unknown): e is SessionExpiredError {
  return e instanceof SessionExpiredError
}

/**
 * Thrown when a search is refused because its targeting is IDENTICAL to the caller's most recent
 * search run, which delivered nothing (`searchInputGate`, HTTP 409 `unchanged-search-input`). It
 * is a product state — change the parameters to search again — not a crash; the surface catches
 * it and shows the localized hint instead of a raw "HTTP 409". Carries the server's German copy
 * as a fallback for any surface that has no localized string of its own.
 */
export class SearchInputUnchangedError extends Error {
  constructor(readonly serverMessage: string) {
    super("search input unchanged")
    this.name = "SearchInputUnchangedError"
  }
}

/** True when a search was refused for identical, no-deliverable targeting (change the parameters). */
export function isSearchInputUnchanged(e: unknown): e is SearchInputUnchangedError {
  return e instanceof SearchInputUnchangedError
}

/** The api's identical-re-run 409 body → the typed error (distinguished from other conflicts). */
async function detectSearchInputUnchanged(res: Response): Promise<SearchInputUnchangedError | null> {
  if (res.status !== 409) return null
  const body = (await res.clone().json().catch(() => null)) as { message?: string; data?: { reason?: string } } | null
  if (body?.data?.reason !== "unchanged-search-input") return null
  return new SearchInputUnchangedError(typeof body.message === "string" ? body.message : "")
}

/**
 * The api's JSON error body → a readable tail for the thrown message. A failure the api
 * NAMED (an unreachable object store, a rejected file type) is worth showing: the surfaces
 * render `error.message` verbatim, so without this the user only ever sees "HTTP 500" while
 * the actionable sentence stays in the response body.
 */
async function serverDetail(res: Response): Promise<string> {
  const body = (await res.clone().json().catch(() => null)) as Record<string, unknown> | null
  if (!body) return ""
  const said = ["error", "detail", "message"]
    .map((key) => body[key])
    .filter((v): v is string => typeof v === "string" && v.length > 0)
  return said.length > 0 ? ` — ${[...new Set(said)].join(": ")}` : ""
}

/** Raise the shared notices (session-expiry prompt, credits 402/403), then throw a typed error. */
async function throwHttpError(res: Response, verb: string, path: string): Promise<never> {
  await reactToSessionExpiry(res, path)
  const block = await reactToEntitlementBlock(res, path)
  if (block) throw new PaymentRequiredError(block)
  const unchanged = await detectSearchInputUnchanged(res)
  if (unchanged) throw unchanged
  if (res.status === 401 && !path.startsWith("/api/auth/")) throw new SessionExpiredError()
  throw new Error(`${verb} ${path} failed: HTTP ${res.status}${await serverDetail(res)}`)
}

/**
 * The metered `/api` paths (procedures behind `meteredGate` — every one debits the wallet). A
 * successful call to any of these changes the balance, so the shared layer fires `CREDITS_UPDATED`
 * once here (NOT per surface) → the shell chip re-fetches. Detached runs (search/generate) accrue
 * cost as they progress; the immediate refresh catches the synchronous debit, and each further
 * metered action (or a navigation) reconciles the rest.
 */
function isMeteredPath(path: string): boolean {
  const p = path.split("?")[0] ?? path
  if (p === "/api/search/start" || p === "/api/applications/generate" || p === "/api/profile/narrative") return true
  if (p === "/api/workshops/search-profile/derive") return true
  return p.startsWith("/api/workshops/") && (p.endsWith("/turn") || p.endsWith("/stimulus") || p.endsWith("/consolidate"))
}

/** POST JSON to a BFF `/api` route with the CSRF header attached. */
export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", [CSRF_HEADER]: "1" },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) await throwHttpError(res, "POST", path)
  clearReloginAttempt() // a healthy authed response → reset the redirect-loop guard.
  const data = (await res.json()) as T
  // A metered action just debited the wallet → nudge the balance chip to re-fetch (shared, once).
  if (isMeteredPath(path) && typeof globalThis.dispatchEvent === "function") {
    globalThis.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT))
  }
  return data
}

/** GET JSON from a BFF `/api` route. */
export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: "application/json" } })
  if (!res.ok) await throwHttpError(res, "GET", path)
  clearReloginAttempt() // a healthy authed response → reset the redirect-loop guard.
  return (await res.json()) as T
}

/** Upload a multipart form to a BFF `/api` route (browser sets the boundary). */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", headers: { [CSRF_HEADER]: "1" }, body: form })
  if (!res.ok) await throwHttpError(res, "UPLOAD", path)
  clearReloginAttempt() // a healthy authed response → reset the redirect-loop guard.
  return (await res.json()) as T
}

// ─── Billing (Step 5) — the typed wallet + checkout client (contract §3) ────────

/** The credit wallet as the api returns it (`GET /billing/wallet`). */
export interface WalletView {
  balance: number
  granted: number
  consumed: number
}

/** A parse error means the api answered but not with a wallet — surface it as "unavailable". */
const numOr = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback)

/** Fetch the caller's credit wallet. Auth-only (never gated / never 402). */
export async function fetchWallet(): Promise<WalletView> {
  const raw = await apiGet<Record<string, unknown>>("/api/billing/wallet")
  return { balance: numOr(raw["balance"], 0), granted: numOr(raw["granted"], 0), consumed: numOr(raw["consumed"], 0) }
}

/** Raised when Stripe isn't configured server-side (503) — the surface shows a calm state. */
export class StripeNotConfiguredError extends Error {
  constructor() {
    super("stripe not configured")
    this.name = "StripeNotConfiguredError"
  }
}

/**
 * Start a hosted Stripe checkout for a credit pack; returns the URL to redirect to. The
 * hosted page is the ONLY payment surface (zero PCI — no Stripe.js, no card fields here).
 * A 503 (`STRIPE_NOT_CONFIGURED`) becomes a typed error so the surface can explain calmly.
 */
export async function startCheckout(packId: string): Promise<{ url: string }> {
  const res = await fetch("/api/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json", [CSRF_HEADER]: "1" },
    body: JSON.stringify({ packId }),
  })
  if (res.status === 503) throw new StripeNotConfiguredError()
  if (!res.ok) await throwHttpError(res, "POST", "/api/billing/checkout")
  const raw = (await res.json()) as { url?: unknown }
  if (typeof raw.url !== "string" || raw.url.length === 0) throw new Error("checkout returned no url")
  return { url: raw.url }
}

/**
 * The evidence store's command transport — maps web-kit optimistic commands onto
 * the api's oRPC verbs. The authoritative reconciling event arrives over the PUSH
 * plane (the review surface subscribes the Evidence stream), so success returns no
 * envelopes; the optimistic patch (ABSOLUTE end-state) stands until push folds it.
 */
export function makeEvidenceCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    "confirm-claim": "/api/evidence/confirm",
    "discard-claim": "/api/evidence/discard",
    "edit-claim": "/api/evidence/edit",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}

/**
 * The asset store's command transport — optimistic confirm/remove predict the
 * ABSOLUTE end-state (status confirmed / removed); the api call persists it and the
 * surface re-fetches `assets.list` to reconcile from server truth. Success returns
 * no envelopes, so the optimistic patch stands until the refresh folds authority.
 */
export function makeAssetCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    "confirm-asset": "/api/assets/confirm",
    "remove-asset": "/api/assets/remove",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}

/**
 * The opportunity store's command transport — optimistic bookmark/dismiss/delete
 * predict the ABSOLUTE end-state (bookmarked / dismissed / removed); the api call
 * persists it and the search surface re-fetches `opportunities.list` to reconcile
 * from server truth. Success returns no envelopes, so the optimistic patch stands
 * until the refresh folds authority.
 */
export function makeOpportunityCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    bookmark: "/api/opportunities/bookmark",
    dismiss: "/api/opportunities/dismiss",
    delete: "/api/opportunities/delete",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}

/**
 * The application command transport — optimistic lifecycle + edit-loop commands. Each predicts the
 * ABSOLUTE end-state (status / edit resolution); the api call persists it and the review surface
 * re-fetches to reconcile from server truth.
 */
export function makeApplicationCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    transition: "/api/applications/transition",
    "edit-approve": "/api/applications/edit/approve",
    "edit-reject": "/api/applications/edit/reject",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}
